import * as React from 'react';
import { Trans } from 'react-i18next';

export type MarkdownStyle = 'bold' | 'italic' | 'breakline' | 'underline' | 'normal';

/**
 * @param i18n i18n library to use
 * @param textId path to access to translated text.
 * @param styles array of strings that represent the styles we want to apply. Right now, we only support bold, italic and underline.
 * @param fillers array of strings to insert into the translated text. Ex. "Hello {{0}} World!" -> "Hello Curious World!" if ['Curious'] is passed
 *                as parameter
 * @param colors you can specify a color to a part of the text
 *
 * HOW IT WORKS
 * In order to apply the right style to the right text, make sure each styled words are
 * surrounded by <index></index>, where index is a number.
 *
 * Input Example (1):
 * Considering a i18n file containing { titles: { mainTitle: Hello <0>world</0> and <1>univers</1> } }
 * <Markdown i18n={i18n} text={'titles.mainTitle'} styles={['bold', 'italic']} />
 * Output:
 * Hello **world** and *univers*.
 *
 * The first style (in this case bold) is applied to the first <index></index> found (in this case world)
 * The second style (in this case italic) is applied to the second <index></index> found (in this case univers)
 *
 * Input Example (2):
 * Considering a i18n file containing { titles: { mainTitle: Hello <0>world</0> and <1>{{0}}</1> } }
 * <Markdown i18n={i18n} text={'titles.mainTitle'} styles={['bold', 'italic']} fillers{['univers']} />
 * Output:
 * Hello **world** and *univers*.
 *
 * Fillers will fill all {{<index>}} elements found in the translated text. Order of elements in the array matters
 */
export const Markdown: React.SFC<{
  colors?: string[];
  fillers?: string[];
  i18n: any;
  styles: MarkdownStyle[];
  text: string;
}> = ({ i18n, text, styles, colors, fillers }) => {
  const components: JSX.Element[] = [];
  if (styles && styles.length > 0) {
    // Compute all required styles
    styles.forEach((style: string, index: number) => {
      let color;
      if (colors && colors.length > index) color = colors[index];

      switch (style) {
        case 'bold': {
          components.push(BoldStyle(index, color));
          break;
        }
        case 'italic': {
          components.push(ItalicStyle(index, color));
          break;
        }
        case 'breakline': {
          components.push(BreakdownStyle(index));
          break;
        }
        case 'underline': {
          components.push(UnderlineStyle(index, color));
          break;
        }
        default: {
          components.push(NormalStyle(index, color));
        }
      }
    });
  }
  return <Trans i18n={i18n} defaults={text} values={generateTextFillers(fillers)} components={components} />;
};

/**
 * Generate an object to use in Trans component.
 * @param fillers String value to insert into the translated text
 * @output Given an array ['hello', 'world'], the output will be { 0: 'hello', 1: 'world'}
 */
const generateTextFillers = (fillers: string[]) => {
  if (!fillers) return [];

  const mapper = {};
  fillers.forEach((value: string, index: number) => (mapper[index] = value));
  return mapper;
};

// Bold Style
const BoldStyle = (index: number, color: string): JSX.Element => (
  <span key={`text${index.toString()}`} style={{ fontWeight: 'bold', color: color }}>
    [{index.toString()}]
  </span>
);

// Italic Style
const ItalicStyle = (index: number, color: string): JSX.Element => (
  <span key={`text${index.toString()}`} style={{ fontStyle: 'italic', color: color }}>
    [{index.toString()}]
  </span>
);

// Breakdown Style
const BreakdownStyle = (index): JSX.Element => <br key={`text${index.toString()}`} style={{ fontSize: '0rem' }} />;

// Underline Style
const UnderlineStyle = (index: number, color: string): JSX.Element => (
  <span key={`text${index.toString()}`} style={{ textDecoration: 'underline', color: color }}>
    [{index.toString()}]
  </span>
);

// Normal Style
const NormalStyle = (index: number, color: string): JSX.Element => (
  <span key={`text${index.toString()}`} style={{ color: color }}>
    [{index.toString()}]
  </span>
);
